import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import ProtectedRoute from "./auth/ProtectedRoute";
import {
  StartPageRedirect,
  SuspenseWrapper,
} from "./components/RouterComponents";
import Layout from "./layout/Layout";
import ReportsWrapper from "./pages/ReportsWrapper";

// Lazy load pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Users = lazy(() => import("./pages/Users/index"));
const Agencies = lazy(() => import("./pages/Agencies/index"));
const Organizations = lazy(() => import("./pages/Organizations/index"));
const Roles = lazy(() => import("./pages/Roles/index"));
const Permissions = lazy(() => import("./pages/Permissions"));

const MyReports = lazy(() => import("./pages/MyReports/index"));
const PlaceholderPage = lazy(() => import("./pages/PlaceholderPage"));
const Prompt = lazy(() => import("./pages/Prompt"));

const Datasources = lazy(() => import("./pages/Datasource/index"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback/index"));
const OnboardingAssist = lazy(() => import("./pages/OnboardingAssist/index"));
const AgencyHubV2 = lazy(() => import("./pages/AgencyConsole/index"));
const AnalyticsDashboard = lazy(
  () => import("./pages/AnalyticsDashboard/index"),
);

// Start page redirect and loading components moved to ./components/RouterComponents

export const router = createBrowserRouter(
  [
    {
      path: "/login",
      element: (
        <SuspenseWrapper>
          <LoginPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: "/forgot-password",
      element: (
        <SuspenseWrapper>
          <ForgotPasswordPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: "/reset-password",
      element: (
        <SuspenseWrapper>
          <ResetPasswordPage />
        </SuspenseWrapper>
      ),
    },
    {
      path: "/",
      element: <Navigate to="/login" replace />,
    },
    // OAuth callback — must be outside ProtectedRoute so the popup can reach it
    // without a valid session. The backend redirects here after provider auth.
    {
      path: "/callback",
      element: (
        <SuspenseWrapper>
          <OAuthCallback />
        </SuspenseWrapper>
      ),
    },
    {
      path: "/app",
      element: (
        <SuspenseWrapper>
          <Layout />
        </SuspenseWrapper>
      ),
      children: [
        {
          index: true,
          element: <StartPageRedirect />,
        },
        // Administration routes
        {
          path: "dashboard",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["dashboards.read"]}>
                <Dashboard />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "users",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["users.read"]}>
                <Users />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "agencies",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["agencies.read"]}>
                <Agencies />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "organizations",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["organizations.read"]}>
                <Organizations />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "agency-console",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={[]}>
                <AgencyHubV2 />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "analytics-dashboard",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["agencies.read"]}>
                <AnalyticsDashboard />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },

        {
          path: "roles",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["users.read"]}>
                <Roles />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "permissions",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["users.read"]}>
                <Permissions />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        // Application routes
        {
          path: "app-dashboard",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["analytics.view"]}>
                <PlaceholderPage
                  title="Dashboard"
                  description="Analytics Dashboard"
                />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "onboarding-assist",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["csv.create"]}>
                <OnboardingAssist />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "reports",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute
                permissions={["reports.read", "reports.read.org"]}
              >
                <ReportsWrapper />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "analytics",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["analytics.view"]}>
                <PlaceholderPage title="Analytics" description="分析" />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "data-sources",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["syncs.read"]}>
                <Datasources />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "my-reports",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["reports.read"]}>
                <MyReports />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
        {
          path: "prompt",
          element: (
            <SuspenseWrapper>
              <ProtectedRoute permissions={["prompts.read"]}>
                <Prompt />
              </ProtectedRoute>
            </SuspenseWrapper>
          ),
        },
      ],
    },
    {
      path: "*",
      element: <Navigate to="/login" replace />,
    },
  ],
  { basename: import.meta.env.BASE_URL },
);
